"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Castle, Palette, History, Globe, Crown, Scroll, Users, Book } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function MarseilleeTarotPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-300">
                <Star className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-red-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-red-500">/</span>
                <span className="text-sm">Tarot de Marseille</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/tarot/systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Tarot de Marseille</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Star className="h-10 w-10 text-red-400" />
                Tarot de Marseille
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                15. yüzyıldan beri süregelen en eski tarot geleneği. Fransız tarot sanatının temelini oluşturan
                ve modern ezoterik tarot kullanımının kökenlerini barındıran klasik sistem.
              </p>
            </div>

            {/* Origins and Development */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kökenler ve Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Historical Origins */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-700 rounded-lg flex items-center justify-center">
                      <History className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Tarihsel Kökenler</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Kuzey İtalya'da 15. yüzyılın başlarında ortaya çıktı. 1499'da Fransa Milano ve Piedmont'u
                    fethettiğinde Güney Fransa'ya tanıtıldı.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">İlk bilinen örnekler 1400'lerin başı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Fransız kültürüne entegrasyonu 1499</span>
                    </div>
                  </div>
                </div>

                {/* Name Origin */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Scroll className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">İsmin Kökeni</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    "Tarot de Marseille" adı 1856'da Fransız kart tarihçisi Romain Merlin tarafından
                    icat edildi. Marseille şehrinin kart üretimindeki rolünü vurgular.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Marseille önemli kart üretim merkezi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">19. yüzyıl terminoloji standardizasyonu</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Deck Structure */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Major Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Majör Arkana (22 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Geleneksel trump kartları. Sade ama güçlü sembolizm ile yaşamın büyük
                    dönüşüm aşamalarını temsil eder.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">I-XXI numaralı kartlar + Le Mat (Deli)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Geleneksel Avrupa tarot sıralaması</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/marseille/major-arcana"
                    className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    22 Majör Arkana Kartını Gör
                  </Link>
                </div>

                {/* Minor Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Minör Arkana (56 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Dört suit'ten oluşan pip kartları. Soyut geometrik desenlerle
                    günlük yaşam enerjilerini temsil eder.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Bâtons, Épées, Coupes, Deniers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Her suit'te As-10 + 4 court kartı</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/marseille/minor-arcana"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    56 Minör Arkana Kartını Gör
                  </Link>
                </div>
              </div>
            </div>

            {/* Visual Characteristics */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Görsel Özellikler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Art Style */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-700 rounded-lg flex items-center justify-center">
                      <Palette className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Sanatsal Stil</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Sade, geometrik tasarım anlayışı. Majör Arkana'da güçlü sembolizm,
                    Minör Arkana'da minimal pip kartları.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Soyut geometrik desenler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Minimal renk paleti</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Sembolik, sade anlatım</span>
                    </div>
                  </div>
                </div>

                {/* Distinctive Features */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Ayırt Edici Özellikler</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Diğer tarot geleneklerinden farklı görsel yaklaşımlar ve
                    kendine özgü sembolik dil kullanır.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Kılıçlar eğimli soyut çizgiler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Değnekler kafes oluşturan düz çizgiler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Benzersiz pip kart tasarımları</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Regional Variations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Bölgesel Varyasyonlar</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Besançon */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                      <Castle className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Besançon Pattern</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-2">
                    Tartışmalı dini imgeleri mitolojik figürlerle değiştirdi.
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Papa → Bacchus</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Papessa → Juno</span>
                    </div>
                  </div>
                </div>

                {/* Piedmontese */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Piedmontese Pattern</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-2">
                    İtalyan kökenli küçük varyasyonlar içeren versiyon.
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-green-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Fool kartında kelebek</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-green-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">İtalyan altyazıları</span>
                    </div>
                  </div>
                </div>

                {/* Lombard */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Crown className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Lombard Pattern</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-2">
                    İtalyan altyazıları ve alternatif kart isimleri kullanan varyasyon.
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">İtalyanca başlıklar</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Yerel adlandırma</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Significance */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Önem</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Oyun Kartları Olarak</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">19. yüzyıla kadar popüler kart oyunları</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Sosyal eğlence aracı olarak kullanım</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Ticari kart üretiminin temeli</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Ezoterik Kullanıma Geçiş</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">18. yüzyıl sonu ezoterik tarot başlangıcı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Modern tarot destelerinin temel kaynağı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Rider-Waite ve Thoth'un etki aldığı gelenek</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modern Usage */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Modern Kullanım</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <p className="text-slate-300 mb-4">
                  Günümüzde Tarot de Marseille, özellikle profesyonel tarot okuyucuları tarafından tercih edilen
                  bir sistem olmaya devam ediyor. Sade tasarımı, okuyucunun kendi sezgisine daha fazla alan bırakır.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="font-semibold text-slate-200 mb-2">Avantajları:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Sezgisel okumaya olanak sağlar</li>
                      <li>• Geleneksel Avrupa tarot sanatını korur</li>
                      <li>• Profesyonel okuyucularca tercih edilir</li>
                      <li>• Oyun kartları ile bağlantısını sürdürür</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-200 mb-2">Zorlukları:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Yeni başlayanlar için zor olabilir</li>
                      <li>• Detaylı görsel ipuçları sınırlı</li>
                      <li>• Hafıza ve sembol bilgisi gerektirir</li>
                      <li>• Modern yorumlama kaynakları az</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Tarot de Marseille sembolik yorumlama aracıdır ve kesinlik iddiası taşımaz. İçerikler tamamen
                merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
                Bu açıklamalar sadece geleneksel sistemin tanıtımı amacıyla sunulmaktadır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/rider-waite"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Rider-Waite-Smith
              </Link>

              <Link
                href="/about/tarot/systems/thoth"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Thoth Tarot
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}